//#region Asset Imports

import * as _ from 'underscore'
//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { Activity, Enums } from '../../../../models/common'
import { OpConsumer, OpLocation } from '../../../../models/core'
import { OpConsumerNotificationCollection, OpConsumerNotificationContent, OpConsumerNotificationToSend } from '../../../../models/custom'

import { ConfigHelper, TypeHelper, ToastHelper, LogHelper, ConvertHelper, LocalizationHelper, TemplateHelper } from '../../../../helpers'
import { ConsumerHelper } from "../../consumers.helper"
import { SystemService } from '../../../../services/system/system.service'
import { UserSessionService } from '../../../../services/user-session'

import { GridOptions, GridController, GridTypes } from '../../../common/grid/grid.controller'


// #region Public Types

export type ConsumerDetailsNotificationsOptions = {
    idConsumer: number
    consumer: OpConsumer
}

// #endregion


export class ConsumerDetailsNotificationsController implements ng.IComponentController {

    // #region Consts

    private static get NOTIFICATIONS_GRID_NAME(): string { return 'consumerDetailsNotificationsGrid' }

    // #endregion


    // #region Members
    public showLoader: boolean

    private options: ConsumerDetailsNotificationsOptions

    private notificationsGridOptions: GridOptions
    private notificationsGridDataSource: DataSource

    private idConsumer: number
    private consumerLocation: OpLocation

    private consumerPersonalData: string
    private chosenDeliveryTypesToShowInPopup: string

    private isLoading: boolean

    private notificationsGrid: GridController<OpConsumerNotificationContent>

    private toolbarOptions: DevExpress.ui.dxToolbarOptions
    private bottomToolbarOptions: DevExpress.ui.dxToolbarOptions

    private notificationsPopup: DevExpress.ui.dxPopup
    private notificationsPopupOptions: DevExpress.ui.dxPopupOptions
    private notificationsFormOptions: DevExpress.ui.dxFormOptions
    private notificationsFormItems: any[]
    private popupToolbarOptions: DevExpress.ui.dxToolbarOptions
    private notificationTextArea: DevExpress.ui.dxTextArea
    private notificationText: string

    private notificationDeliveryTypes: Enums.NotificationDeliveryType[] = []
    private notificationToSendData: OpConsumerNotificationToSend

    private notificationCollection: OpConsumerNotificationCollection
    private notificationCollectionToSend: OpConsumerNotificationCollection

    private notificationsPopupButton: DevExpress.ui.dxButton
    private chosenRecipient: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChosenRecipient)
    private chosenDeliveryTypes: string = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChosenDeliveryTypes)

    // #endregion


    // #region Ctor

    constructor(
        private userSession: UserSessionService,
        private systemService: SystemService
    ) {

    }

    // #endregion


    // #region IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    private initialize(): void {

        if (this.options != null) {
            if (this.options.idConsumer != null) {
                this.getNotifications(this.options.idConsumer)
                    .then((result: OpConsumerNotificationContent[]) => {
                        this.notificationsGridDataSource = new DataSource(result)
                    })
                    .fail(() => {
                        this.notificationsGridDataSource = null
                    })
                    .always(() => {
                        this.initializeNotificationsGrid()

                        this.setToolbarParameters()
                        this.setBottomToolbarOptions()
                        this.updateNotificationButtonDisabled()

                        this.setNotificationsPopupOptions()
                        this.setNotificationsFormItems()
                        this.setNotificationsFormOptions()
                        this.setNotificationsPopupToolbarOptions()
                    })
            }
        }
    }

    private refresh(): void {
        this.notificationsPopup = null
        this.notificationsPopupOptions = null
        this.notificationsFormOptions = null
        this.notificationsFormItems = null
        this.popupToolbarOptions = null
        this.notificationTextArea = null
        this.notificationDeliveryTypes = null
        this.notificationCollection = null
        this.notificationCollectionToSend = null

        this.initialize()
    }

    private initializeNotificationsGrid(): void {
        this.notificationsGridOptions = {
            addCustomFilters: true,
            buttonsOptions: {
                enabled: true,
            },
            columns: ConsumerHelper.getNotificationsGridColumns(),
            name: ConsumerDetailsNotificationsController.NOTIFICATIONS_GRID_NAME,
            type: GridTypes.MAIN,
            onInitialized: (e: any, gridController: GridController<OpConsumerNotificationContent>) => { this.notificationsGrid = gridController },
            dataSource: this.notificationsGridDataSource,
            editing: {
                mode: "cell",
                allowUpdating: true
            },
            ignoreKey: true,
        }
    }


    // #region Notification Grid

    private getNotifications(idConsumer: number): JQueryPromise<OpConsumerNotificationContent[]> {
        return this.userSession.getDataProvider()
            .Consumer
            .getConsumerNotifications(idConsumer)
            .then((result: OpConsumerNotificationCollection) => {
                this.notificationCollection = result
                this.notificationCollectionToSend = this.notificationCollection.clone(true, false, false, false)

                return result.notifications
            })
    }

    private setOpStates(): void {

        let isGridItems: boolean
        let gridItems: OpConsumerNotificationContent[]

        if (this.notificationsGrid != null && this.notificationsGrid.dxGrid.getDataSource().items() != null) {
            isGridItems = true
            gridItems = this.notificationsGrid.dxGrid.getDataSource().items()
        } else {
            isGridItems = false
        }

        if (this.notificationCollectionToSend != null && this.notificationCollectionToSend.notifications != null && isGridItems) {
            const beforeChanges: OpConsumerNotificationContent[] = _.sortBy(this.notificationCollectionToSend.notifications, (notification: OpConsumerNotificationContent) => notification.idNotificationDeliveryType)
            const afterChanges: OpConsumerNotificationContent[] = _.sortBy(gridItems, (notification: OpConsumerNotificationContent) => notification.idNotificationDeliveryType)

            _.forEach(beforeChanges, (beforeChange: OpConsumerNotificationContent, index: number) => {
                const afterChange = afterChanges[index]

                if (beforeChange.isActive !== afterChange.isActive) {
                    afterChange.opState = Enums.OpChangeState.Modified
                }
            })

            this.notificationCollection.notifications = afterChanges
        }
    }

    private saveNotifications(): JQueryPromise<OpConsumerNotificationContent[]> {
        this.setOpStates()

        // Avatar jest nullowany, ponieważ powoduje to problem przy deserializacji.
        if (this.notificationCollection != null && this.notificationCollection.operator != null && this.notificationCollection.operator.actor != null) {
            this.notificationCollection.operator.actor.avatar = null
        }

        return this.userSession.getDataProvider().Consumer.saveConsumerNotifications(this.notificationCollection)
            .then((result: OpConsumerNotificationCollection) => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, Enums.SimaxModuleName.Consumer))

                return result.notifications
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(ConsumerHelper.LOG_PREFIX, `Unable to save notification ${errorMessage}.`)
            })
            .always(() => {
                this.refresh()
            })
    }

    public setToolbarParameters() {
        if (this.options.consumer != null && this.options.consumer.actor != null) {
            this.consumerPersonalData = this.options.consumer.actor.toString()
        }

        this.setToolbarOptions()
    }

    public setToolbarOptions(): void {
        this.toolbarOptions = {
            items: [
                {
                    location: 'before',
                    locateInMenu: 'never',
                    template: '<div>{{$ctrl.consumerPersonalData}}</div>'
                },
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        template: TemplateHelper.getIcon(ConfigHelper.Icon.SEND, this.userSession),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendNotification),
                        onInitialized: (e: any) => {
                            this.notificationsPopupButton = e.component
                            this.updateNotificationButtonDisabled()
                        },
                        onClick: () => {
                            this.onSendNotificationClick()
                        },
                        visible: this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_SEND_NOTIFICATION_OP)
                    }
                },
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        template: TemplateHelper.getIcon(ConfigHelper.Icon.DETAILS, this.userSession),
                        hint: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SHOW_DETAILS, Enums.SimaxModuleName.Actor),
                        onClick: () => {
                            this.openActorDetails()
                        }
                    }
                }
            ]
        }
    }

    private setBottomToolbarOptions(): void {
        this.bottomToolbarOptions = {
            items: [
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
                        onClick: (e: any): void => {
                            this.saveNotifications()
                        }
                    }
                }
            ]
        }
    }

    private openActorDetails(): void {
        if (this.options.consumer != null) {
            this.systemService.openDetails({
                moduleName: Enums.SimaxModuleName.Actor,
                referenceObjectId: this.options.consumer.idActor
            })
        }
    }

    public updateNotificationButtonDisabled(): void {
        if (this.notificationsPopupButton != null) {
            let isActive: boolean = false

            if (this.notificationCollectionToSend != null && this.notificationCollectionToSend.notifications != null) {
                isActive = _.some(this.notificationCollectionToSend.notifications, ((content: OpConsumerNotificationContent) => content.isActive))
            }

            this.notificationsPopupButton.option('disabled', !isActive)
        }
    }

    // #endregion


    // #region Notification Form

    public showNotificationsPopup(): void {
        this.notificationsPopup.option('visible', true)

    }

    public hideNotificationsPopup(): void {
        this.notificationsPopup.option('visible', false)
    }

    public setNotificationsPopupOptions(): void {
        this.notificationsPopupOptions = {
            onInitialized: (e: any) => {
                this.notificationsPopup = e.component
            },
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SendNotification),
            showTitle: true,
            width: 400,
            height: 400,
            showCloseButton: true
        }

    }

    public setNotificationsFormOptions(): void {
        this.notificationsFormOptions = {
            requiredMark: '*',
            showRequiredMark: true,
            colCount: 1,
            height: '100%',
            width: '100%',
            labelLocation: 'top',
            items: this.notificationsFormItems,
        }
    }

    public setNotificationsFormItems(): void {
        this.notificationsFormItems = [
            <DevExpress.ui.dxFormSimpleItem>{
                visibleIndex: 1,
                colSpan: 1,
                label: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationText)
                },
                editorType: "dxTextArea",
                editorOptions: {
                    onInitialized: (e: any) => {
                        this.notificationTextArea = e.component
                    },
                    height: 200
                }
            }
        ]
    }

    public setNotificationsPopupToolbarOptions(): void {
        this.popupToolbarOptions = {
            items: [
                {
                    location: 'after',
                    widget: 'dxButton',
                    options: {
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Apply),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Apply),
                        onClick: () => {
                            this.notificationText = this.notificationTextArea.option('value')
                            this.setNotificationData()
                            this.sendNotification()
                        },
                        disabled: !this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_SEND_NOTIFICATION_OP)
                    }
                }
            ]
        }
    }

    public setNotificationData(): void {
        this.notificationToSendData = new OpConsumerNotificationToSend({
            IdConsumer: this.options.idConsumer,
            Text: this.notificationText,
            NotificationDeliveryTypes: this.notificationDeliveryTypes
        })
    }

    public setNotificationDeliveryTypes(): void {
        this.notificationDeliveryTypes = []

        if (this.notificationCollectionToSend != null && this.notificationCollectionToSend.notifications != null) {
            this.notificationCollectionToSend.notifications.forEach((content: OpConsumerNotificationContent) => {
                if (content.isActive == true) {
                    this.notificationDeliveryTypes.push(content.idNotificationDeliveryType)
                }
            })
        }
    }

    public onSendNotificationClick(): void {
        this.setNotificationDeliveryTypes()
        this.setChosenDeliveryTypesToShowInPopup()
        this.showNotificationsPopup()
    }

    public setChosenDeliveryTypesToShowInPopup(): void {
        this.chosenDeliveryTypesToShowInPopup = ''
        this.notificationDeliveryTypes.forEach(type => {
            this.chosenDeliveryTypesToShowInPopup = this.chosenDeliveryTypesToShowInPopup + Enums.NotificationDeliveryType[type] + ', '
        })
        this.chosenDeliveryTypesToShowInPopup = this.chosenDeliveryTypesToShowInPopup.substring(0, this.chosenDeliveryTypesToShowInPopup.length - 2)
    }

    public sendNotification(): void {
        this.showLoader = true
        this.userSession.getDataProvider().Consumer.
            sendConsumerNotifications(this.notificationToSendData)
            .then((result: boolean) => {
                if (result) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationSent))
                } else {
                    ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationSendFailed))
                }

                this.hideNotificationsPopup()
            })
            .fail((error: any) => {
                const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)

                ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(ConsumerHelper.LOG_PREFIX, `Unable to send notification ${errorMessage}.`)
            })
            .always(() => {
                this.showLoader = false
            })
    }

    // #endregion
}